import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import Grid from '@mui/material/Grid'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import dayjs, { Dayjs } from 'dayjs'
import { BackButton } from '../../components/molecules/BackButton'
import { DatePicker } from '../../components/molecules/DatePicker'
import DownloadIcon from '@mui/icons-material/Download'
import {
  useJahisNsipsStatsQuery,
  useHeadquartersPharmaciesQuery,
} from '../../features/jahisNsipsMatching'
import {
  JahisNsipsFieldStat,
  FIELD_CATEGORIES,
  DISPLAY_FIELDS,
} from '../../types/models/JahisNsipsMatching'
import { localStorageWrapper } from '../../helpers/localStorage'
import { exportJahisNsipsMatchSummaryCSV } from '../../apis/jahisNsipsMatching'

/**
 * Dayjs から ISO 8601 週表記 "YYYY-Www" を生成
 */
const toIsoWeek = (d: Dayjs): string => {
  const date = new Date(Date.UTC(d.year(), d.month(), d.date())) as Date & {
    getUTCDay: () => number
  }
  const dayNum = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const weekNum = Math.ceil(
    ((date.valueOf() - yearStart.valueOf()) / 86400000 + 1) / 7
  )
  return `${date.getUTCFullYear()}-W${String(weekNum).padStart(2, '0')}`
}

/**
 * 一致率を表示用にフォーマット
 */
const formatMatchRate = (rate: number): string => {
  return `${(rate * 100).toFixed(1)}%`
}

/**
 * フィールド別統計テーブル行
 */
const FieldStatRow: FC<{
  stat: JahisNsipsFieldStat
  onDetailClick: (field: string) => void
}> = ({ stat, onDetailClick }) => {
  const theme = useTheme()
  const hasMismatches = stat.mismatch_count > 0

  return (
    <TableRow>
      <TableCell>{stat.label}</TableCell>
      <TableCell align="right">{formatMatchRate(stat.match_rate)}</TableCell>
      <TableCell align="right">
        {stat.mismatch_count.toLocaleString()}件
      </TableCell>
      <TableCell align="center">
        {hasMismatches ? (
          <Button
            size="small"
            variant="outlined"
            onClick={() => onDetailClick(stat.field)}
            sx={{
              color: theme.palette.grey[600],
              borderColor: theme.palette.grey[400],
              fontWeight: 'bold',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
                borderColor: theme.palette.grey[500],
              },
            }}
          >
            詳細
          </Button>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        )}
      </TableCell>
    </TableRow>
  )
}

/**
 * JAHIS-NSIPS突合 統計ダッシュボードページ
 */
export const JahisNsipsStatsPage: FC = () => {
  const navigate = useNavigate()
  const isHeadquarters = localStorageWrapper.getItem('is_headquarters') ?? false

  // 日付範囲（デフォルト: 過去30日）
  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(30, 'day')
  )
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs())

  // 薬局選択（本部アカウントのみ）
  const [selectedPharmacyId, setSelectedPharmacyId] = useState<string>('')
  const { data: pharmacyData } = useHeadquartersPharmaciesQuery(isHeadquarters)

  // 本部向け集計 CSV 同期 DL
  const [isExporting, setIsExporting] = useState(false)
  const handleExportSummary = async () => {
    if (!startDate || !endDate) return
    setIsExporting(true)
    try {
      const response = await exportJahisNsipsMatchSummaryCSV({
        start_week: toIsoWeek(startDate),
        end_week: toIsoWeek(endDate),
        ...(selectedPharmacyId ? { pharmacy_ids: [selectedPharmacyId] } : {}),
      })
      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8-sig',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `nsips_matching_summary_${dayjs().format('YYYYMMDD')}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('CSV 出力に失敗しました', e)
      alert(
        'CSV 出力に失敗しました。権限・期間を確認してから再度お試しください。'
      )
    } finally {
      setIsExporting(false)
    }
  }

  // 統計取得
  const {
    data: stats,
    isPending,
    isError,
    error,
  } = useJahisNsipsStatsQuery(
    startDate?.format('YYYY-MM-DD'),
    endDate?.format('YYYY-MM-DD'),
    selectedPharmacyId || undefined
  )

  // 不一致詳細ページへ遷移
  const handleDetailClick = (field: string) => {
    const params = new URLSearchParams({
      start_date: startDate?.format('YYYY-MM-DD') || '',
      end_date: endDate?.format('YYYY-MM-DD') || '',
      field,
    })
    if (selectedPharmacyId) {
      params.set('pharmacy_id', selectedPharmacyId)
    }
    navigate(`/portal/jahis-nsips-mismatches?${params.toString()}`)
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100%',
        py: 4,
        px: { xs: 2, sm: 4 },
        overflow: 'auto',
        maxWidth: '100vw',
        boxSizing: 'border-box',
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          position: 'fixed',
          zIndex: 1300,
          left: 0,
          top: 0,
          bgcolor: 'rgba(66, 66, 66, 0.75)',
          color: '#fff',
          p: 0.8,
          height: '36px',
          borderRadius: '0px 0px 8px 0px',
          display: 'flex',
          alignItems: 'center',
          boxShadow: '0px 2px 4px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(2px)',
          paddingRight: '12px',
        }}
      >
        <CompareArrowsIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
        <Typography variant="body2">JAHIS-NSIPS突合 統計</Typography>
      </Box>

      {/* ローディング */}
      {isPending && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Box sx={{ mt: 6, mb: 10, maxWidth: '1200px', mx: 'auto' }}>
        {/* 期間選択 */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            期間選択
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 5 }}>
              <DatePicker
                label="開始日"
                date={startDate}
                onChange={setStartDate}
              />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 2 }}
              sx={{
                textAlign: 'center',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              <Typography>~</Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 5 }}>
              <DatePicker label="終了日" date={endDate} onChange={setEndDate} />
            </Grid>
          </Grid>
        </Paper>

        {/* 薬局選択（本部アカウントのみ） */}
        {isHeadquarters && pharmacyData && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              店舗選択
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="pharmacy-select-label">薬局</InputLabel>
              <Select
                labelId="pharmacy-select-label"
                value={selectedPharmacyId}
                label="薬局"
                onChange={(e) => setSelectedPharmacyId(e.target.value)}
              >
                <MenuItem value="">
                  <em>自店舗</em>
                </MenuItem>
                {pharmacyData.pharmacies.map((pharmacy) => (
                  <MenuItem key={pharmacy.id} value={pharmacy.id}>
                    {pharmacy.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>
        )}

        {/* 本部アカウント向け 集計 CSV 出力 */}
        {isHeadquarters && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              集計 CSV 出力（本部）
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              選択期間に含まれる週の、店舗 × 項目 × 週次の一致率と
              不具合報告件数を 1 ファイルで出力します。店舗未選択時は
              法人配下の全店舗が対象です。
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportSummary}
              disabled={isExporting || !startDate || !endDate}
            >
              {isExporting ? '出力中…' : '集計 CSV を出力'}
            </Button>
          </Paper>
        )}

        {/* 選択中の薬局名表示 */}
        {stats?.pharmacy && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {stats.pharmacy.name} のデータを表示中
          </Alert>
        )}

        {/* エラー表示 */}
        {isError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            データの取得に失敗しました: {(error as Error)?.message}
          </Alert>
        )}

        {/* 統計データ */}
        {stats && (
          <>
            {/* カテゴリ別統計テーブル */}
            {FIELD_CATEGORIES.map((category) => {
              // このカテゴリに該当するフィールドの統計を取得（表示対象のみ）
              const categoryStats = stats.field_stats.filter(
                (stat) =>
                  category.fields.includes(stat.field) &&
                  (DISPLAY_FIELDS as readonly string[]).includes(stat.field)
              )

              if (categoryStats.length === 0) return null

              return (
                <Paper key={category.category} sx={{ p: 3, mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    {category.category}
                  </Typography>
                  <TableContainer>
                    <Table sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '40%' }}>項目</TableCell>
                          <TableCell align="right" sx={{ width: '20%' }}>
                            一致率
                          </TableCell>
                          <TableCell align="right" sx={{ width: '20%' }}>
                            不一致
                          </TableCell>
                          <TableCell align="center" sx={{ width: '20%' }}>
                            操作
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {categoryStats.map((stat) => (
                          <FieldStatRow
                            key={stat.field}
                            stat={stat}
                            onDetailClick={handleDetailClick}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )
            })}
          </>
        )}
      </Box>

      <BackButton text="管理ポータルに戻る" />
    </Box>
  )
}
